/**
 * How a task and a run document are read, in one place (D23).
 *
 * There are now two listeners per collection — the work assigned to you and
 * the work you opened — and they return the same documents in a different
 * slice, so the mapping cannot live inside either hook. Both shapes carry the
 * ownership fields the lenses filter on, because "who is this for" is only
 * answerable from the document.
 */
import { clampRoutinePoints } from '@x9/routine-template-model';

export function toMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  const ts = value as { toMillis?: () => number; seconds?: number };
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/**
 * A picture the browser can actually load. Several of the fields below hold a
 * device path (`file:///data/user/0/com.hwyah.x9/…`) on a phone and a download
 * URL once the upload lands, and rendering the former gives a broken image
 * where a placeholder was wanted.
 */
function httpStr(value: unknown): string | null {
  const raw = str(value);
  return raw && /^https?:\/\//.test(raw.trim()) ? raw.trim() : null;
}

/**
 * The task's picture, in the order native's `resolveTaskThumbnailUri` prefers:
 * the thumbnail render first, then progressively larger ones. `photoUri` is
 * the odd one — local while capture is in flight, a download URL afterwards —
 * so it is admitted only when it is already a URL.
 */
function latLng(value: unknown): { latitude: number; longitude: number } | null {
  if (!value || typeof value !== 'object') return null;
  const loc = value as { latitude?: unknown; longitude?: unknown };
  const latitude = typeof loc.latitude === 'number' ? loc.latitude : NaN;
  const longitude = typeof loc.longitude === 'number' ? loc.longitude : NaN;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

function phoneLast4(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const raw = (value as { phoneLast4?: unknown }).phoneLast4;
  if (typeof raw !== 'string') return null;
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 4 ? digits.slice(-4) : null;
}

function taskPhoto(data: Record<string, unknown>): string | null {
  return (
    httpStr(data.thumbnailUrl) ??
    httpStr(data.compressedUrl) ??
    httpStr(data.photoUri) ??
    httpStr(data.mediumUrl) ??
    httpStr(data.photoUrl) ??
    httpStr(data.imageUrl)
  );
}

/** The review card is a large 3:4 frame, so it wants the 1200px render, not the 150px thumb. */
function taskHeroPhoto(data: Record<string, unknown>): string | null {
  return (
    httpStr(data.compressedUrl) ??
    httpStr(data.mediumUrl) ??
    httpStr(data.photoUri) ??
    httpStr(data.photoUrl) ??
    httpStr(data.imageUrl) ??
    httpStr(data.thumbnailUrl)
  );
}

export interface AssignedTask {
  id: string;
  title: string;
  description: string;
  status: string;
  bucketId: string | null;
  /**
   * Denormalised on the task at creation. The only name a contributor can read:
   * they are not a member of the bucket they filed into, so the bucket document
   * itself is closed to them.
   */
  bucketName: string | null;
  photoUrl: string | null;
  /** Larger render for the review card. Lists keep `photoUrl`. */
  heroPhotoUrl: string | null;
  /**
   * The task's execute link, stamped on the doc at creation (D15). Present
   * means the home can open the task without first trading its id for a link,
   * which is a whole round trip and a spinner. Null for tasks created before
   * eager minting, which still take the trade.
   */
  linkShortId: string | null;
  /** `tasks.userId` — who opened it. Named for what it means. */
  ownerUserId: string | null;
  assignedUserId: string | null;
  /** Set as soon as a contact is picked, before that contact is a user. */
  assignedContactId: string | null;
  /**
   * Denormalised assignee avatar. Native prefers this on the photo overlay so
   * a profile change lands without a second lookup.
   */
  assigneePhotoURL?: string | null;
  /** `routine` marks a run mirror, `routineTemplate` a routine's step. */
  source: string | null;
  /**
   * The next four are meaningful only on `routineTemplate` steps, where they
   * are the routine's shape: which heading the step sits under, what it is
   * worth, and what the executor must produce. Read here rather than through a
   * second listener — the owner's tasks are already in this snapshot.
   */
  sectionId: string | null;
  points: number;
  checkRequireNote: boolean;
  checkRequirePhoto: boolean;
  checkYesRequireNote: boolean;
  checkYesRequirePhoto: boolean;
  /**
   * The routine a step belongs to. Its own field, not `bucketId` — verified
   * against production, where template steps carry `routineBucketId` and no
   * `bucketId` at all, which is why the routine page has always found its
   * steps through the bucket's `taskIds` instead.
   */
  routineBucketId: string | null;
  /**
   * Native delete is a tombstone, not a removal (D8). The document stays so
   * phones can reconcile; the web must skip it the same way native's merge
   * does, or a deleted step stays in the list with its title stripped.
   */
  deleted: boolean;
  /**
   * Web-capture review. `pending` is the moderation queue; it is not a
   * member of `bucket.taskIds` until approve. Null for everything else.
   */
  moderationStatus: string | null;
  /** `anonymous-web` or `phone-web` for QR / capture submissions. */
  submissionSource: string | null;
  /** Zone tag copied from the QR at capture. Stable if the QR is renamed. */
  sourceQrTag: string | null;
  sourceQrToken: string | null;
  /** Staging path under `webSubmissions/` — Review loads it via a callable. */
  photoStoragePath: string | null;
  /** Pin the review card shows. `location`, then the submitter's own GPS. */
  location: { latitude: number; longitude: number } | null;
  highlightSvg: string | null;
  /** Signed-in contributor. Anonymous web submissions have none. */
  submitterUid: string | null;
  /** Last four digits only — a full number never reaches the screen. */
  submitterPhoneLast4: string | null;
  /** False when the submitter bypassed the geofence check. */
  locationVerified: boolean | null;
  updatedAt: number;
  createdAt: number;
}

export function toTask(id: string, data: Record<string, unknown>): AssignedTask {
  return {
    id,
    title: typeof data.title === 'string' ? data.title : '',
    description: typeof data.description === 'string' ? data.description : '',
    status: typeof data.status === 'string' ? data.status : 'pending',
    bucketId: str(data.bucketId),
    bucketName: str(data.bucketName),
    photoUrl: taskPhoto(data),
    heroPhotoUrl: taskHeroPhoto(data),
    // `taskLinkShortId` is the legacy alias the server dual-writes; either is
    // the same execute link.
    linkShortId: str(data.taskLinkExecuteShortId) ?? str(data.taskLinkShortId),
    ownerUserId: str(data.userId),
    assignedUserId: str(data.assignedUserId),
    assignedContactId: str(data.assignedContactId),
    assigneePhotoURL: httpStr(data.assigneePhotoURL),
    source: str(data.source),
    sectionId: str(data.sectionId),
    points: clampRoutinePoints(data.points),
    checkRequireNote: data.checkRequireNote === true,
    checkRequirePhoto: data.checkRequirePhoto === true,
    checkYesRequireNote: data.checkYesRequireNote === true,
    checkYesRequirePhoto: data.checkYesRequirePhoto === true,
    routineBucketId: str(data.routineBucketId),
    deleted: data.deleted === true,
    moderationStatus: str(data.moderationStatus),
    submissionSource: str(data.submissionSource),
    sourceQrTag: str(data.sourceQrTag),
    sourceQrToken: str(data.sourceQrToken),
    photoStoragePath: str(data.photoStoragePath),
    location: latLng(data.location) ?? latLng(data.submitterLocation),
    highlightSvg: str(data.highlightSvg),
    submitterUid: str(data.submitterUid),
    submitterPhoneLast4: phoneLast4(data.submitterContact),
    locationVerified: data.locationVerified === false ? false : data.locationVerified === true ? true : null,
    updatedAt: toMillis(data.updatedAt),
    createdAt: toMillis(data.createdAt),
  };
}

/** Native tombstones stay in Firestore; nothing that lists work should show them. */
export function isLiveTask<T extends { deleted?: boolean }>(task: T): boolean {
  return task.deleted !== true;
}

export interface RunOutlineStep {
  stepId: string;
  order: number;
  title: string;
  status: string;
  outcome: 'yes' | 'no' | null;
  sectionId: string | null;
  sectionName: string | null;
  note: string | null;
  photoUrl: string | null;
  description?: string | null;
  points?: number;
  proofUrls?: string[];
  completedAt?: number | string | null;
}

export interface RunOutlineSection {
  sectionId: string;
  name: string;
  order: number;
}

export interface AssignedRun {
  id: string;
  bucketId: string | null;
  bucketName: string;
  /** The newest step evidence, standing in for a picture of the run. */
  photoUrl: string | null;
  state: string;
  stepsTotal: number;
  stepsResolved: number;
  /** Resolved with nothing wrong — the ring's cyan. */
  stepsClean: number;
  /** Denied or checked no — the ring's magenta. */
  stepsIssues: number;
  /** Embedded steps + section headings, for the desktop review modal. */
  outlineSteps: RunOutlineStep[];
  outlineSections: RunOutlineSection[];
  /**
   * The run's web link, minted at dispatch (D15). Present means the home opens
   * the run without trading its id for a link first. Null for runs dispatched
   * before eager minting, and for reassigned runs whose link was revoked.
   */
  linkShortId: string | null;
  ownerUserId: string | null;
  executorUserId: string | null;
  executorContactId: string | null;
  startedAt?: number;
  acceptedAt?: number;
  runCompletedAt?: number;
  updatedAt: number;
  createdAt: number;
}

/**
 * A run is one document with an embedded `steps[]`, each carrying a `status`.
 * Resolved means no further executor work; mirrors `RESOLVED_STEP_STATUSES` in
 * `@x9/routine-run-model` and `isStepResolved` in `../execute/runEvidence`.
 *
 * Note `step.outcome` is not the field to read — it is check-mode only and null
 * until a yes/no lands.
 */
const RESOLVED_STEP_STATUSES = ['completed', 'denied', 'checkedYes', 'checkedNo'];

/**
 * Resolved steps are split the way native's run donut splits them
 * (`runStepsStatusDistribution`): a step that came back done is not the same
 * news as one that came back denied or checked no, and folding the two into a
 * single "resolved" count would hide a reported problem inside progress.
 */
const ISSUE_STEP_STATUSES = ['denied', 'checkedNo'];

function countSteps(data: Record<string, unknown>): {
  total: number;
  resolved: number;
  clean: number;
  issues: number;
} {
  const raw = data.steps;
  if (!Array.isArray(raw)) return { total: 0, resolved: 0, clean: 0, issues: 0 };
  const steps = raw.filter(Boolean);
  let clean = 0;
  let issues = 0;
  for (const step of steps) {
    const status = (step as { status?: unknown }).status;
    if (typeof status !== 'string' || !RESOLVED_STEP_STATUSES.includes(status)) continue;
    if (ISSUE_STEP_STATUSES.includes(status)) issues += 1;
    else clean += 1;
  }
  return { total: steps.length, resolved: clean + issues, clean, issues };
}

/**
 * A run has no picture of its own — native draws a mosaic of its step
 * thumbnails. The web takes the first one that exists, which is the newest
 * evidence the run has produced; before any step is done there is none, and the
 * row falls back to the bucket's image or the folder glyph.
 */
function runPhoto(data: Record<string, unknown>): string | null {
  const raw = data.steps;
  if (!Array.isArray(raw)) return null;
  for (const step of raw) {
    if (!step) continue;
    const found = httpStr((step as Record<string, unknown>).thumbnailUrl);
    if (found) return found;
  }
  return null;
}

function outlineFrom(data: Record<string, unknown>): {
  steps: RunOutlineStep[];
  sections: RunOutlineSection[];
} {
  const rawSteps = Array.isArray(data.steps) ? data.steps : [];
  const steps: RunOutlineStep[] = rawSteps.filter(Boolean).map((row, index) => {
    const step = row as Record<string, unknown>;
    const outcome: RunOutlineStep['outcome'] =
      step.outcome === 'yes' || step.outcome === 'no' ? step.outcome : null;
    return {
      stepId: str(step.stepId) || `step_${index}`,
      order: typeof step.order === 'number' ? step.order : index,
      title: typeof step.title === 'string' && step.title ? step.title : 'Untitled',
      status: typeof step.status === 'string' ? step.status : 'pending',
      outcome,
      sectionId: str(step.sectionId),
      sectionName: str(step.sectionName),
      note: str(step.details) ?? str(step.note),
      description: str(step.description),
      points: typeof step.points === 'number' ? step.points : 1,
      proofUrls: Array.isArray(step.proofUrls)
        ? step.proofUrls.map((url) => httpStr(url)).filter((url): url is string => !!url)
        : [],
      completedAt: toMillis(step.completedAt) || null,
      photoUrl:
        httpStr(step.thumbnailUrl) ??
        httpStr(step.compressedUrl) ??
        httpStr(step.mediumUrl) ??
        httpStr(step.photoUri),
    };
  });
  const rawSections = Array.isArray(data.sectionsSnapshot) ? data.sectionsSnapshot : [];
  const sections = rawSections.filter(Boolean).map((row, index) => {
    const section = row as Record<string, unknown>;
    return {
      sectionId: str(section.sectionId) || `sec_${index}`,
      name: typeof section.name === 'string' && section.name ? section.name : 'Section',
      order: typeof section.order === 'number' ? section.order : index,
    };
  });
  return { steps, sections };
}

export function toRun(id: string, data: Record<string, unknown>): AssignedRun {
  const steps = countSteps(data);
  const outline = outlineFrom(data);
  return {
    id,
    bucketId: str(data.bucketId),
    bucketName: typeof data.bucketName === 'string' ? data.bucketName : 'Routine',
    photoUrl: runPhoto(data),
    state: typeof data.state === 'string' ? data.state : 'pending',
    stepsTotal: steps.total,
    stepsResolved: steps.resolved,
    stepsClean: steps.clean,
    stepsIssues: steps.issues,
    outlineSteps: outline.steps,
    outlineSections: outline.sections,
    linkShortId: str(data.runLinkShortId),
    ownerUserId: str(data.ownerUserId),
    executorUserId: str(data.executorUserId),
    executorContactId: str(data.executorContactId),
    startedAt: toMillis(data.startedAt),
    acceptedAt: toMillis(data.acceptedAt),
    runCompletedAt: toMillis(data.completedAt),
    updatedAt: toMillis(data.updatedAt),
    createdAt: toMillis(data.createdAt),
  };
}

/**
 * Work you opened *and* were assigned arrives from both listeners. Keeping both
 * copies would double it on the board, so the first one wins — they are the
 * same document read twice.
 */
export function mergeById<T extends { id: string }>(...lists: T[][]): T[] {
  const seen = new Map<string, T>();
  for (const list of lists) {
    for (const item of list) {
      if (!seen.has(item.id)) seen.set(item.id, item);
    }
  }
  return [...seen.values()];
}
